/**
 * Сервис для шифрования и дешифрования файлов с использованием Web Crypto API
 * Реализует клиентское шифрование для безопасного архивирования файлов
 */

// Константы для алгоритмов шифрования
const ALGORITHM = 'AES-GCM';
const KEY_LENGTH = 256; // длина ключа в битах
const SALT_LENGTH = 16; // длина соли в байтах
const IV_LENGTH = 12; // длина вектора инициализации в байтах
const ITERATION_COUNT = 100000; // количество итераций для PBKDF2

/**
 * Генерирует случайные байты заданной длины
 * @param {number} length - Длина массива байтов
 * @returns {Uint8Array} - Массив случайных байтов
 */
const getRandomBytes = length => {
  return crypto.getRandomValues(new Uint8Array(length));
};

/**
 * Преобразует пароль в ключ с использованием PBKDF2
 * @param {string} password - Пароль пользователя
 * @param {Uint8Array} salt - Соль для PBKDF2
 * @returns {Promise<CryptoKey>} - Ключ для шифрования/дешифрования
 */
const deriveKey = async (password, salt) => {
  // Преобразуем пароль в формат ArrayBuffer
  const encoder = new TextEncoder();
  const passwordBuffer = encoder.encode(password);

  // Создаем материал ключа из пароля
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    passwordBuffer,
    { name: 'PBKDF2' },
    false,
    ['deriveKey']
  );

  // Выводим ключ с использованием PBKDF2
  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt,
      iterations: ITERATION_COUNT,
      hash: 'SHA-256',
    },
    keyMaterial,
    {
      name: ALGORITHM,
      length: KEY_LENGTH,
    },
    false, // не экспортируемый
    ['encrypt', 'decrypt']
  );
};

/**
 * Шифрует файл с использованием пароля
 * @param {File} file - Файл для шифрования
 * @param {string} password - Пароль для шифрования
 * @returns {Promise<Object>} - Объект с зашифрованными данными и метаданными
 */
export const encryptFile = async (file, password) => {
  try {
    console.log(`Начинаем шифрование файла: ${file.name} (${file.size} байт, тип: ${file.type})`);

    // Генерируем соль и вектор инициализации
    const salt = getRandomBytes(SALT_LENGTH);
    const iv = getRandomBytes(IV_LENGTH);

    console.log('Соль и вектор инициализации сгенерированы');

    // Получаем ключ из пароля
    const key = await deriveKey(password, salt);
    console.log('Ключ шифрования получен из пароля');

    // Читаем содержимое файла
    const fileBuffer = await file.arrayBuffer();
    console.log(`Файл прочитан в буфер (${fileBuffer.byteLength} байт)`);

    // Шифруем данные
    console.log('Начинаем шифрование данных...');
    const encryptedData = await crypto.subtle.encrypt(
      {
        name: ALGORITHM,
        iv,
      },
      key,
      fileBuffer
    );
    console.log(`Данные зашифрованы (${encryptedData.byteLength} байт)`);

    // Создаем заголовок с метаданными
    const header = {
      version: 1,
      algorithm: ALGORITHM,
      keyLength: KEY_LENGTH,
      iterationCount: ITERATION_COUNT,
      fileName: file.name,
      fileType: file.type || 'application/octet-stream',
      fileSize: file.size,
      encryptionDate: new Date().toISOString(),
    };

    // Преобразуем заголовок в JSON и затем в ArrayBuffer
    const headerString = JSON.stringify(header);
    const encoder = new TextEncoder();
    const headerBuffer = encoder.encode(headerString);

    // Создаем буфер для хранения всех данных:
    // [4 байта длины заголовка][заголовок][соль][IV][зашифрованные данные]
    const headerLengthBuffer = new ArrayBuffer(4);
    new DataView(headerLengthBuffer).setUint32(0, headerBuffer.byteLength, true);

    // Объединяем все части в один ArrayBuffer
    const resultBuffer = new Uint8Array(
      headerLengthBuffer.byteLength +
        headerBuffer.byteLength +
        salt.byteLength +
        iv.byteLength +
        encryptedData.byteLength
    );

    let offset = 0;

    // Копируем длину заголовка
    resultBuffer.set(new Uint8Array(headerLengthBuffer), offset);
    offset += headerLengthBuffer.byteLength;

    // Копируем заголовок
    resultBuffer.set(headerBuffer, offset);
    offset += headerBuffer.byteLength;

    // Копируем соль
    resultBuffer.set(salt, offset);
    offset += salt.byteLength;

    // Копируем IV
    resultBuffer.set(iv, offset);
    offset += iv.byteLength;

    // Копируем зашифрованные данные
    resultBuffer.set(new Uint8Array(encryptedData), offset);

    console.log(
      `Создан финальный буфер с зашифрованными данными (${resultBuffer.byteLength} байт)`
    );

    // Возвращаем результат
    return {
      salt: Array.from(salt),
      iv: Array.from(iv),
      encryptedData: resultBuffer.buffer,
      fileName: file.name,
      fileType: file.type || 'application/octet-stream',
      fileSize: file.size,
      encryptionDate: new Date().toISOString(),
    };
  } catch (error) {
    console.error('Ошибка при шифровании файла:', error);
    throw new Error('Не удалось зашифровать файл: ' + error.message);
  }
};

/**
 * Дешифрует файл с использованием пароля
 * @param {Object} encryptedFileData - Объект с зашифрованными данными и метаданными
 * @param {string} password - Пароль для дешифрования
 * @returns {Promise<File>} - Дешифрованный файл
 */
export const decryptFile = async (encryptedFileData, password) => {
  try {
    console.log('Начинаем дешифрование файла');

    // Получаем данные из буфера
    const encryptedBuffer = encryptedFileData.encryptedData;
    const dataView = new DataView(encryptedBuffer);

    // Читаем длину заголовка (первые 4 байта)
    const headerLength = dataView.getUint32(0, true);
    console.log(`Длина заголовка: ${headerLength} байт`);

    // Читаем заголовок
    const headerBytes = new Uint8Array(encryptedBuffer, 4, headerLength);
    const decoder = new TextDecoder();
    const headerString = decoder.decode(headerBytes);
    const header = JSON.parse(headerString);
    console.log('Заголовок прочитан:', header);

    // Вычисляем смещения для соли, IV и зашифрованных данных
    let offset = 4 + headerLength;

    // Читаем соль
    const salt = new Uint8Array(encryptedBuffer, offset, SALT_LENGTH);
    offset += SALT_LENGTH;

    // Читаем IV
    const iv = new Uint8Array(encryptedBuffer, offset, IV_LENGTH);
    offset += IV_LENGTH;

    // Читаем зашифрованные данные
    const encryptedContent = new Uint8Array(encryptedBuffer, offset);

    console.log(
      `Соль (${salt.byteLength} байт), IV (${iv.byteLength} байт) и зашифрованные данные (${encryptedContent.byteLength} байт) извлечены`
    );

    // Получаем ключ из пароля
    const key = await deriveKey(password, salt);
    console.log('Ключ дешифрования получен из пароля');

    // Дешифруем данные
    console.log('Начинаем дешифрование данных...');
    const decryptedBuffer = await crypto.subtle.decrypt(
      {
        name: ALGORITHM,
        iv,
      },
      key,
      encryptedContent
    );
    console.log(`Данные успешно дешифрованы (${decryptedBuffer.byteLength} байт)`);

    // Создаем файл из дешифрованных данных
    const fileName = header.fileName || 'decrypted_file';
    const fileType = header.fileType || 'application/octet-stream';

    return new File([decryptedBuffer], fileName, { type: fileType });
  } catch (error) {
    console.error('Ошибка при дешифровании файла:', error);
    throw new Error('Не удалось дешифровать файл: ' + error.message);
  }
};

/**
 * Архивирует и шифрует несколько файлов
 * @param {File[]} files - Массив файлов для архивирования и шифрования
 * @param {string} password - Пароль для шифрования
 * @returns {Promise<Object>} - Объект с зашифрованным архивом и метаданными
 */
export const encryptAndArchiveFiles = async (files, password) => {
  try {
    // Здесь будет реализация архивирования файлов перед шифрованием
    // Для этого потребуется библиотека для работы с ZIP в браузере
    // Например, JSZip (https://stuk.github.io/jszip/)

    // Пример реализации будет добавлен в следующих файлах

    // Пока просто шифруем первый файл как пример
    return await encryptFile(files[0], password);
  } catch (error) {
    console.error('Ошибка при архивировании и шифровании файлов:', error);
    throw new Error('Не удалось архивировать и зашифровать файлы: ' + error.message);
  }
};

/**
 * Проверяет доступность Web Crypto API в браузере
 * @returns {boolean} - true, если API доступен
 */
export const isCryptoSupported = () => {
  return window.crypto && window.crypto.subtle ? true : false;
};
